import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { analytics } from './firebase';
import { logEvent } from 'firebase/analytics';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import AuthAction from './components/AuthAction';
import Profile from './components/Profile';
import Playbooks from './components/Playbooks';
import Picks from './components/Picks';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import FAQItem from './components/FAQItem';
import Footer from './components/Footer';
import PlaybookBuilder from './components/PlaybookBuilder';
import SimpleBuilder from './components/SimpleBuilder';
import CanvasCursor from './components/CanvasCursor';
import HeroStatement from './components/HeroStatement';
import PlaybookView from './components/PlaybookView';
import NotFound from './components/NotFound';
import LanguageBuilder from './components/LanguageBuilder';
import TopPlaybooks from './components/TopPlaybooks';
import AdminPortal from './components/AdminPortal';
import { isSuperAdminEmail } from './constants/admin';
import { SiteContentProvider, useSiteContent } from './contexts/SiteContentContext';
import SiteBanner from './components/SiteBanner';
import './App.css';

// Protected Route component
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="page-loading">Loading…</div>;
  }

  return user ? children : <Navigate to="/login" />;
}

// Public Route component (redirects to home if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="page-loading">Loading…</div>;
  }

  return !user ? children : <Navigate to="/" />;
}

// Where pick sellers and Odikai differ; Odikai's side is drawn from the approach notes
const comparisonRows = [
  {
    topic: 'Where picks come from',
    others: 'A tipster, or an opaque AI model you have to take on trust.',
    odikai: 'Your own mix of betting strategies, turned into a playbook that is tested on real past fights and tuned to make the most of every dollar.'
  },
  {
    topic: 'What picks cost',
    others: 'A subscription. If their picks made money, why would they sell them?',
    odikai: 'Nothing. Our AI predictions are free, and always will be.'
  },
  {
    topic: 'How much to bet',
    others: "They tell you who to back, but not how much of your bankroll to put on each pick.",
    odikai: 'Enter your budget and every pick comes with a stake, sized by how the strategies behind it have performed in the past.'
  },
  {
    topic: 'Time in the app',
    others: 'Apps want you there constantly: betting more when you lose, depositing more when you run out.',
    odikai: 'Enter your budget, pick your strategy, place your bets, then close the app and enjoy your life.'
  }
];


// Home component
function Home() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { content } = useSiteContent();

  const handleFAQClick = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="home">
      <section className="hero" ref={heroRef}>
        <CanvasCursor containerRef={heroRef} />
        <div className="hero-inner">
          <div className="hero-content">
            <h1>{content.hero.title}</h1>
            {content.hero.subtitle && <p className="hero-subtitle">{content.hero.subtitle}</p>}
            <div className="hero-actions">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/picks')}>
                View picks
              </button>
              <button className="btn btn-lg" onClick={() => navigate('/top-playbooks')}>
                Compare playbooks
              </button>
            </div>
          </div>
          <HeroStatement />
        </div>
      </section>

      <section className="compare-section">
        <div className="home-section">
          <h2 className="section-title">Winning picks don't guarantee long-term profits.</h2>
          <p className="section-lede">
            Anyone can pick a few winners, and AI prediction tools are a dime a dozen. We take a different approach.
          </p>
          <table className="compare">
            <thead>
              <tr>
                <th scope="col"><span className="visually-hidden">Topic</span></th>
                <th scope="col">Pick sellers and betting apps</th>
                <th scope="col" className="compare-us">
                  <span className="compare-us-label">
                    <img src="/images/logo.png" alt="" />
                    Odikai
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.topic}>
                  <th scope="row">{row.topic}</th>
                  <td data-label="Pick sellers and betting apps">{row.others}</td>
                  <td data-label="Odikai" className="compare-us">{row.odikai}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="faq-band">
        <div className="home-section faq-wrap">
          <h2 className="section-title">Common questions</h2>
          <div className="faq-list">
            {content.home_faq.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => handleFAQClick(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="etymology-section">
        <div className="home-section">
          <p className="definition">
            The <span className="term">Hellan<span className="term-highlight">odikai</span></span> (Ancient Greek: Ἑλλανοδίκαι) were the judges of the Ancient Olympic Games, and the success of the games was attributed to their efforts.
            <a href="https://en.wikipedia.org/wiki/Hellanodikai" className="source-link" target="_blank" rel="noopener noreferrer">(wikipedia)</a>
          </p>
        </div>
      </section>
    </div>
  );
}

const navLinkClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;

// Main App Layout component
function AppLayout() {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const mainRef = useRef(null);

  // Track page views
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_path: pathname,
      page_title: document.title
    });
  }, [pathname]);

  // Scroll to top when pathname changes
  useEffect(() => {
    // Force scroll to top in multiple ways
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [pathname]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  const handleLogout = async () => {
    closeMenu();
    try {
      await logout();
    } catch (error) {
      // Remove all console.error statements
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.classList.toggle('menu-open');
  };

  useEffect(() => {
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);

  return (
    <div className="app">
      <nav className={`site-nav${isMenuOpen ? ' open' : ''}`} aria-label="Main">
        <div className="site-nav-inner">
          <Link to="/" className="brand" onClick={closeMenu}>
            <img src="/images/logo.png" alt="" />
            <span>Odikai</span>
          </Link>
          <button
            className="menu-toggle"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className="nav-menu">
            <ul className="nav-links">
              <li><NavLink to="/picks" className={navLinkClass} onClick={closeMenu}>Picks</NavLink></li>
              <li><NavLink to="/top-playbooks" className={navLinkClass} onClick={closeMenu}>Top playbooks</NavLink></li>
              {user && (
                <li><NavLink to="/playbooks" className={navLinkClass} onClick={closeMenu}>My playbooks</NavLink></li>
              )}
              <li><NavLink to="/faq" className={navLinkClass} onClick={closeMenu}>FAQ</NavLink></li>
            </ul>
            <ul className="nav-account">
              {user ? (
                <>
                  {isSuperAdminEmail(user) && (
                    <li><NavLink to="/admin" className={navLinkClass} onClick={closeMenu}>Admin</NavLink></li>
                  )}
                  <li><NavLink to="/profile" className={navLinkClass} onClick={closeMenu}>Profile</NavLink></li>
                  <li><button onClick={handleLogout} className="nav-link">Log out</button></li>
                </>
              ) : (
                <>
                  <li><NavLink to="/login" className={navLinkClass} onClick={closeMenu}>Log in</NavLink></li>
                  <li><Link to="/register" className="btn btn-primary btn-sm" onClick={closeMenu}>Create account</Link></li>
                </>
              )}
            </ul>
          </div>
        </div>
      </nav>

      <SiteBanner />

      <main ref={mainRef}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Root App component
function App() {
  return (
    <Router>
      <AuthProvider>
        <SiteContentProvider>
        <Routes>
          <Route element={<AppLayout />}>
            <Route index element={<Home />} />
            <Route path="/picks" element={<Picks />} />
            <Route path="/top-playbooks" element={<TopPlaybooks />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/auth/action" element={<AuthAction />} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/playbooks" element={<PrivateRoute><Playbooks /></PrivateRoute>} />
            <Route path="/playbooks/:id" element={<PlaybookView />} />
            <Route path="/playbooks/build" element={<PrivateRoute><PlaybookBuilder /></PrivateRoute>} />
            <Route path="/playbooks/build/simple" element={<PrivateRoute><SimpleBuilder /></PrivateRoute>} />
            <Route path="/playbooks/build/language" element={<PrivateRoute><LanguageBuilder /></PrivateRoute>} />
            <Route path="/contact" element={<PrivateRoute><Contact /></PrivateRoute>} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/admin" element={<PrivateRoute><AdminPortal /></PrivateRoute>} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        </SiteContentProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
